import { z } from 'zod';

import {
  AvailStateSchema,
  CoupleModeSchema,
  EventResourceSchema,
  IdSchema,
  RoleSexSchema,
  ScheduleEventSchema,
} from './resources';

/**
 * Geração automática em fila: o pedido cria uma execução (`schedule_runs`), o worker
 * percorre as etapas do `solver/model.md` e grava a proposta; nada muda na escala até o
 * "Salvar rascunho" / "Publicar" (`apply`).
 */

export const GenerationCriteriaSchema = z
  .object({
    balance: z.boolean(),
    rotation: z.boolean(),
    repeat: z.boolean(),
    couples: z.boolean(),
    individual: z.boolean(),
    history: z.boolean(),
  })
  .meta({ id: 'GenerationCriteria', description: 'Critérios de otimização ligados.' });

export const GenerationOptionsSchema = z
  .object({
    ministryIds: z.array(IdSchema),
    disabledRoleIds: z.array(IdSchema),
    limit: z.number().int().min(1).max(31).meta({ description: 'Máximo de escalas por membro no mês.' }),
    allowUnset: z.boolean(),
    criteria: GenerationCriteriaSchema,
    excludedMemberIds: z
      .array(IdSchema)
      .max(500)
      .optional()
      .meta({ description: 'Simulação "e se" aplicada: membros fora desta geração.' }),
    needOverrides: z
      .record(IdSchema, z.number().int().min(0).max(50))
      .optional()
      .meta({ description: 'Simulação "e se" aplicada: roleId → quantidade em todos os eventos gerados.' }),
  })
  .meta({ id: 'GenerationOptions' });

export const GenerationRequestSchema = z
  .object({ eventIds: z.array(IdSchema).min(1).max(200), options: GenerationOptionsSchema })
  .meta({ id: 'GenerationRequest' });

export const DecisionSchema = z
  .object({
    eventId: IdSchema,
    roleId: IdSchema,
    ministryId: IdSchema,
    memberId: IdSchema,
    availability: AvailStateSchema,
    weekday: z.number().int(),
    period: z.union([z.literal(0), z.literal(1), z.literal(2)]),
    load: z.number().int(),
    minLoad: z.number().int(),
    gapDays: z.number().int().nullable(),
    withSpouse: z.boolean(),
    coupleMode: CoupleModeSchema.nullable(),
    partnerId: IdSchema.nullable().meta({
      description: 'O parceiro de "servem juntos" que ficou na mesma função do evento.',
    }),
    candidates: z.number().int(),
    nextId: IdSchema.nullable(),
    nextLoad: z.number().int().nullable(),
    excluded: z.object({
      used: z.number().int(),
      sex: z.number().int(),
      blackout: z.number().int(),
      unavailable: z.number().int(),
      limit: z.number().int(),
      rules: z.number().int().meta({ description: 'Fora pelas regras avançadas da função.' }),
    }),
    allowedSex: RoleSexSchema,
    enabledCount: z.number().int(),
    experience: z.number().int().meta({ description: 'Escalas anteriores da pessoa nesta função.' }),
    mentorId: IdSchema.nullable().meta({ description: 'Em treinamento: quem a acompanha na função.' }),
    critical: z.boolean(),
  })
  .meta({ id: 'Decision', description: 'Por que cada pessoa foi escolhida — a matriz de decisão.' });

export const PendingSlotSchema = z
  .object({
    eventId: IdSchema,
    roleId: IdSchema,
    ministryId: IdSchema,
    missing: z.number().int(),
    reason: z.string(),
    critical: z.boolean(),
  })
  .meta({ id: 'PendingSlot' });

export const GenerationResultSchema = z
  .object({
    events: z.array(ScheduleEventSchema).meta({ description: 'Só os eventos gerados, já preenchidos.' }),
    eventIds: z.array(IdSchema),
    filled: z.number().int(),
    needed: z.number().int(),
    criticalFilled: z.number().int(),
    criticalNeeded: z.number().int(),
    pending: z.array(PendingSlotSchema),
    preferenceHits: z.number().int(),
    couples: z.number().int(),
    pairs: z
      .number()
      .int()
      .meta({ description: 'Pares que servem juntos (regra da função) escalados juntos na mesma função.' }),
    load: z.record(IdSchema, z.number().int()),
    decisions: z.array(DecisionSchema),
  })
  .meta({ id: 'GenerationResult' });

export const RUN_STEPS = [
  'analyzing_availability',
  'distributing',
  'checking_conflicts',
  'optimizing_balance',
  'finalizing',
] as const;

export const ScheduleRunSchema = z
  .object({
    id: IdSchema,
    status: z.enum(['queued', 'running', 'done', 'failed', 'applied']),
    step: z.enum(RUN_STEPS).nullable(),
    error: z.string().nullable(),
    createdAt: z.iso.datetime(),
    result: GenerationResultSchema.nullable(),
  })
  .meta({ id: 'ScheduleRun' });

export const ProposalAdjustmentSchema = z
  .object({ eventId: IdSchema, roleId: IdSchema, memberId: IdSchema, fromRoleId: IdSchema.nullable() })
  .meta({
    id: 'ProposalAdjustment',
    description:
      'Troca feita à mão na prévia: a pessoa entra na vaga pendente, vindo de `fromRoleId` (ou livre).',
  });

export const ApplyRunRequestSchema = z
  .object({ publish: z.boolean(), adjustments: z.array(ProposalAdjustmentSchema).max(200).optional() })
  .meta({ id: 'ApplyRunRequest' });

export const ApplyRunResponseSchema = z
  .object({ events: z.array(EventResourceSchema), message: z.string() })
  .meta({ id: 'ApplyRunResponse', description: 'Os eventos com a escala gerada já gravada.' });

export type GenerationRequest = z.infer<typeof GenerationRequestSchema>;
export type GenerationResultDto = z.infer<typeof GenerationResultSchema>;
export type ScheduleRun = z.infer<typeof ScheduleRunSchema>;
export type ApplyRunResponse = z.infer<typeof ApplyRunResponseSchema>;
export type RunStep = (typeof RUN_STEPS)[number];
